import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, Signal } from '@angular/core';
import { DataService } from '@app/shared/services/data-service';
import { IndustryDTO } from '@app/shared/services/dtos/company-industry/company-industry.dto';
import { CompanyPaginatedUsersResponseDTO } from '@app/shared/services/dtos/company-industry/company-paginated-industries-response.dto';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { CompanyIndustriesPageData } from '@app/shared/types/models/company-industries-page-data';
import {
  CreateCompanyIndustryRequestBody,
  UpdateCompanyIndustryRequestBody
} from '@app/shared/types/models/company-industry/company-industry-dto.model';
import { Industry } from '@app/shared/types/models/industry/industry.model';
import { AppEvent } from '@app/shared/types/models/notifications/app-event';
import { CompanyUsersPageParams } from '@app/shared/types/models/page-params';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { PageData } from '@app/store/page-data';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { ActiveCompanyService } from './active-company.service';
import { CompanyIndustryFactory } from './factories/company-industry-factory.service';

export const getPageKey = (
  companyId: string,
  page: number,
  limit: number,
  sortField: string,
  sortDir: string,
  search: string
): string =>
  `${companyId}-${page}-${limit}-${convertToKeyString(
    sortField
  )}-${convertToKeyString(sortDir)}-${convertToKeyString(search)}`;

export const convertToKeyString = (value: string): string => (value ? value.trim().toLowerCase() : '');

/**
 * CompanyIndustriesDataService - Company-scoped industries: store, pagination, and HTTP-backed mutations.
 *
 * When {@link ActiveCompanyService.activeCompanyId} is set, loads industries for that company (constructor `effect`)
 * instead of reacting to notification events.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Company industries views, modals, and related CRUD flows.
 *
 * Architecture:
 * - {@link CompanyIndustriesDataService}: Caching, queries, and mutations
 * - {@link ActiveCompanyService}: Triggers initial load for the active company
 */
@Injectable({ providedIn: 'root' })
export class CompanyIndustriesDataService extends DataService<IndustryDTO, Industry> {
  private readonly companyIndustriesPageList: DataStore<PageData> = new DataStore<PageData>();
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly companyIndustryFactory: CompanyIndustryFactory = inject(CompanyIndustryFactory);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);

  private industries: DataStore<Industry> = new DataStore<Industry>();
  private companyIndustries: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  public constructor() {
    super();
    effect(() => {
      const companyId = this.activeCompanyService.activeCompanyId();
      if (!companyId) {
        return;
      }
      this.loadCompanyIndustries(companyId);
    });
  }

  public getCompanyIndustries(companyId: string): Signal<Industry[] | undefined> {
    if (!companyId) {
      return signal(undefined);
    }

    return computed(
      () =>
        this.companyIndustries
          .get(companyId)()
          ?.ids?.map(id => this.industries.get(id)()) || []
    );
  }

  public getCompanyIndustriesPageList(
    pageParams: CompanyUsersPageParams
  ): Signal<CompanyIndustriesPageData | undefined> {
    if (!pageParams.companyId) {
      return signal(undefined);
    }

    return computed(() => {
      const companyUsersPageId = this.companyIndustriesPageList.get(
        getPageKey(
          pageParams.companyId,
          pageParams.page,
          pageParams.limit,
          pageParams.sortField,
          pageParams.sortDir,
          pageParams.search
        )
      )();
      return {
        total: companyUsersPageId?.total,
        id: companyUsersPageId?.id,
        page: companyUsersPageId?.page,
        limit: companyUsersPageId?.limit,
        content: companyUsersPageId?.ids.map(id => this.industries.get(id)())
      };
    });
  }

  public getById(industryId: string): Signal<Industry | undefined> {
    return computed(() => this.industries.get(industryId)());
  }

  protected handleEvent(event: AppEvent): void {
    void event;
  }

  public loadCompanyIndustries(companyId: string): void {
    if (this.companyIndustries.hasDataForId(companyId)) {
      return;
    }
    this.fetchCompanyIndustries(companyId);
  }

  public loadCompanyIndustriesPages(pageParams: CompanyUsersPageParams): void {
    if (
      this.companyIndustriesPageList.hasDataForId(
        getPageKey(
          pageParams.companyId,
          pageParams.page,
          pageParams.limit,
          pageParams.sortField,
          pageParams.sortDir,
          pageParams.search
        )
      )
    ) {
      return;
    }
    this.fetchCompanyIndustriesPageList(pageParams);
  }

  private fetchCompanyIndustriesPageList(pageParams: CompanyUsersPageParams): void {
    this.loaderService.startLoading();
    const params: Record<string, string | number> = {
      page: pageParams.page,
      limit: pageParams.limit,
      sort: pageParams.sortField,
      order: pageParams.sortDir
    };
    if (pageParams.search) {
      params['search'] = pageParams.search;
    }
    this.httpClient
      .get<CompanyPaginatedUsersResponseDTO>(`${environment.APIEndPoint}industries`, {
        params
      })
      .subscribe({
        next: (response: CompanyPaginatedUsersResponseDTO) => {
          this.companyIndustriesPageList.upsert({
            id: getPageKey(
              pageParams.companyId,
              pageParams.page,
              pageParams.limit,
              pageParams.sortField,
              pageParams.sortDir,
              pageParams.search
            ),
            ids: response.items.map(itm => itm.id),
            total: response.total,
            limit: response.limit,
            page: response.page
          });

          this.loaderService.stopLoading();
        },
        error: () => this.loaderService.stopLoading(),
        complete: () => this.loaderService.stopLoading()
      });
  }

  public deleteCompanyIndustry(pageParams: CompanyUsersPageParams, companyIndustry: Industry): void {
    this.loaderService.startLoading();

    this.httpClient.delete(environment.APIEndPoint + `industries/${companyIndustry.id}`).subscribe({
      next: () => {
        this.industries.delete(companyIndustry.id);
        const existingCompanyUsers: string[] =
          this.companyIndustriesPageList.get(
            getPageKey(
              pageParams.companyId,
              pageParams.page,
              pageParams.limit,
              pageParams.sortField,
              pageParams.sortDir,
              pageParams.search
            )
          )().ids ?? [];
        this.companyIndustriesPageList.upsert({
          id: getPageKey(
            pageParams.companyId,
            pageParams.page,
            pageParams.limit,
            pageParams.sortField,
            pageParams.sortDir,
            pageParams.search
          ),
          ids: existingCompanyUsers.filter(existingUser => existingUser !== companyIndustry.id),
          total:
            this.companyIndustriesPageList.get(
              getPageKey(
                pageParams.companyId,
                pageParams.page,
                pageParams.limit,
                pageParams.sortField,
                pageParams.sortDir,
                pageParams.search
              )
            )()?.total - 1,
          limit: this.companyIndustriesPageList.get(
            getPageKey(
              pageParams.companyId,
              pageParams.page,
              pageParams.limit,
              pageParams.sortField,
              pageParams.sortDir,
              pageParams.search
            )
          )()?.limit,
          page: this.companyIndustriesPageList.get(
            getPageKey(
              pageParams.companyId,
              pageParams.page,
              pageParams.limit,
              pageParams.sortField,
              pageParams.sortDir,
              pageParams.search
            )
          )()?.page
        });

        this.toastService.success(
          this.translateService.instant('company-industries.toasts.successfully-deleted', {
            value: companyIndustry.name
          })
        );
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }

  public addCompanyIndustry(pageParams: CompanyUsersPageParams, request: CreateCompanyIndustryRequestBody): void {
    this.loaderService.startLoading();
    this.httpClient.post<IndustryDTO>(environment.APIEndPoint + 'industries', request).subscribe({
      next: (companyIndustryDTO: IndustryDTO) => {
        this.upsertLocalData(companyIndustryDTO);
        const existingCompanyIndustries: string[] =
          this.companyIndustriesPageList.get(
            getPageKey(
              pageParams.companyId,
              pageParams.page,
              pageParams.limit,
              pageParams.sortField,
              pageParams.sortDir,
              pageParams.search
            )
          )().ids ?? [];
        this.companyIndustriesPageList.upsert({
          id: getPageKey(
            pageParams.companyId,
            pageParams.page,
            pageParams.limit,
            pageParams.sortField,
            pageParams.sortDir,
            pageParams.search
          ),
          ids: existingCompanyIndustries.concat(companyIndustryDTO.id),
          total: this.companyIndustriesPageList.get(
            getPageKey(
              pageParams.companyId,
              pageParams.page,
              pageParams.limit,
              pageParams.sortField,
              pageParams.sortDir,
              pageParams.search
            )
          )()?.total,
          limit: this.companyIndustriesPageList.get(
            getPageKey(
              pageParams.companyId,
              pageParams.page,
              pageParams.limit,
              pageParams.sortField,
              pageParams.sortDir,
              pageParams.search
            )
          )()?.limit,
          page: this.companyIndustriesPageList.get(
            getPageKey(
              pageParams.companyId,
              pageParams.page,
              pageParams.limit,
              pageParams.sortField,
              pageParams.sortDir,
              pageParams.search
            )
          )()?.page
        });
        this.toastService.success(
          this.translateService.instant('company-industries.toasts.successfully-created', {
            value: companyIndustryDTO.name
          })
        );
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }

  public updateCompanyIndustry(companyIndustryId: string, request: UpdateCompanyIndustryRequestBody): void {
    this.loaderService.startLoading();
    this.httpClient.put<IndustryDTO>(environment.APIEndPoint + `industries/${companyIndustryId}`, request).subscribe({
      next: (companyIndustryDTO: IndustryDTO) => {
        this.upsertLocalData(companyIndustryDTO);
        this.toastService.success(
          this.translateService.instant('company-industries.toasts.successfully-edited-name', {
            value: companyIndustryDTO.name
          })
        );
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }

  protected fetchCompanyIndustries(companyId: string): void {
    this.loaderService.startLoading();

    this.httpClient
      .get<CompanyPaginatedUsersResponseDTO>(
        environment.APIEndPoint + `industries?page=0&limit=99999&sort=name&order=Desc`
      )
      .subscribe({
        next: (companyIndustries: CompanyPaginatedUsersResponseDTO) => {
          this.industries.upsertMany(companyIndustries.items.map(itm => this.companyIndustryFactory.produce(itm)));

          this.companyIndustries.upsert({
            id: companyId,
            ids: companyIndustries.items.map(itm => itm.id)
          });
        },
        error: () => this.loaderService.stopLoading(),
        complete: () => this.loaderService.stopLoading()
      });
  }

  public upsertLocalData(dto: IndustryDTO): Signal<Industry> {
    return dto ? this.industries.upsert(this.companyIndustryFactory.produce(dto)) : signal(undefined);
  }
}
